import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const OrderContext = createContext();

const SESSION_DURATION = 5 * 60 * 60 * 1000; // 5 hours in milliseconds

// Helper function to generate unique session ID
const generateSessionId = (restaurantId) => {
  return `order_${restaurantId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to clean expired orders from localStorage
const cleanExpiredOrders = () => {
  const keys = Object.keys(localStorage);
  const now = Date.now();
  
  keys.forEach(key => {
    if (key.startsWith('order_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && data.expiresAt && now > data.expiresAt) {
          localStorage.removeItem(key);
          console.log(`Cleaned expired order: ${key}`);
        }
      } catch (e) {
        // Invalid data, remove it
        localStorage.removeItem(key);
      }
    }
  });
};

export const OrderProvider = ({ children, restaurantId }) => {
  const [orderItems, setOrderItems] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize order session
  useEffect(() => {
    if (!restaurantId) return;

    // Clean expired orders first
    cleanExpiredOrders();

    // Look for existing session for this restaurant
    const existingSessionKey = `currentOrderSession_${restaurantId}`;
    let existingSessionId = localStorage.getItem(existingSessionKey);
    
    if (existingSessionId) {
      // Check if session data exists and is not expired
      try {
        const orderData = JSON.parse(localStorage.getItem(existingSessionId));
        if (orderData && Date.now() < orderData.expiresAt) {
          // Valid session found, restore it
          setSessionId(existingSessionId);
          setOrderItems(orderData.items || []);
          setIsInitialized(true);
          return;
        } else {
          // Expired or invalid, clean it up
          localStorage.removeItem(existingSessionId);
          localStorage.removeItem(existingSessionKey);
        }
      } catch (e) {
        // Invalid data, clean it up
        localStorage.removeItem(existingSessionId);
        localStorage.removeItem(existingSessionKey);
      }
    }

    // Create new session
    const newSessionId = generateSessionId(restaurantId);
    setSessionId(newSessionId);
    localStorage.setItem(existingSessionKey, newSessionId);
    setIsInitialized(true);
  }, [restaurantId]);

  // Save order to localStorage whenever it changes
  useEffect(() => {
    if (!sessionId || !isInitialized) return;

    const total = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const orderData = {
      sessionId,
      restaurantId,
      timestamp: Date.now(),
      expiresAt: Date.now() + SESSION_DURATION,
      items: orderItems,
      total
    };

    localStorage.setItem(sessionId, JSON.stringify(orderData));
  }, [orderItems, sessionId, restaurantId, isInitialized]);

  // Calculate total price
  const calculateTotal = useCallback(() => {
    return orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [orderItems]);

  // Add item to order
  const addItem = useCallback((item) => {
    setOrderItems(prevItems => {
      // Check if item already exists
      const existingItemIndex = prevItems.findIndex(i => i.item_id === item.item_id);
      
      if (existingItemIndex > -1) {
        // Item exists, increase quantity
        const newItems = [...prevItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + 1
        };
        return newItems;
      } else {
        // New item, add with quantity 1
        return [...prevItems, { ...item, quantity: 1 }];
      }
    });
  }, []);

  // Remove item from order
  const removeItem = useCallback((itemId) => {
    setOrderItems(prevItems => prevItems.filter(item => item.item_id !== itemId));
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }

    setOrderItems(prevItems => {
      const newItems = [...prevItems];
      const itemIndex = newItems.findIndex(i => i.item_id === itemId);
      
      if (itemIndex > -1) {
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          quantity
        };
      }
      
      return newItems;
    });
  }, [removeItem]);

  // Increase quantity
  const increaseQuantity = useCallback((itemId) => {
    setOrderItems(prevItems => {
      const newItems = [...prevItems];
      const itemIndex = newItems.findIndex(i => i.item_id === itemId);
      
      if (itemIndex > -1) {
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          quantity: newItems[itemIndex].quantity + 1
        };
      }
      
      return newItems;
    });
  }, []);

  // Decrease quantity
  const decreaseQuantity = useCallback((itemId) => {
    setOrderItems(prevItems => {
      const newItems = [...prevItems];
      const itemIndex = newItems.findIndex(i => i.item_id === itemId);
      
      if (itemIndex > -1) {
        const currentQuantity = newItems[itemIndex].quantity;
        if (currentQuantity > 1) {
          newItems[itemIndex] = {
            ...newItems[itemIndex],
            quantity: currentQuantity - 1
          };
          return newItems;
        } else {
          // Quantity would be 0, remove item
          return prevItems.filter(item => item.item_id !== itemId);
        }
      }
      
      return prevItems;
    });
  }, []);

  // Clear all items
  const clearOrder = useCallback(() => {
    setOrderItems([]);
  }, []);

  // Get item quantity
  const getItemQuantity = useCallback((itemId) => {
    const item = orderItems.find(i => i.item_id === itemId);
    return item ? item.quantity : 0;
  }, [orderItems]);

  // Get total item count
  const getTotalItemCount = useCallback(() => {
    return orderItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [orderItems]);

  // Memoize calculated values
  const total = useMemo(() => calculateTotal(), [calculateTotal]);
  const itemCount = useMemo(() => getTotalItemCount(), [getTotalItemCount]);

  const value = useMemo(() => ({
    orderItems,
    addItem,
    removeItem,
    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    clearOrder,
    getItemQuantity,
    getTotalItemCount,
    total,
    itemCount
  }), [
    orderItems,
    addItem,
    removeItem,
    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    clearOrder,
    getItemQuantity,
    getTotalItemCount,
    total,
    itemCount
  ]);

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

// Custom hook to use order context
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
